/**
 * Instant-Funding pair risk formulas.
 *
 * Pure functions. No I/O. Importable from worker, dashboard, DO, and
 * (later) the manager kill-switch so every layer derives the same
 * numbers from the same inputs.
 *
 * Rule recap (locked 2026-04-28):
 *   • 1% max floating PnL per position, anchored to *current* balance,
 *     refreshes after each closed position. Unique to instant-funding.
 *   • daily DD: cumulative realised + floating, anchored to start-of-day
 *     equity. e.g. 2% on Maven 10k, 3% on IF Micro 5k.
 *   • lifetime DD: anchored to *starting* balance, fixed for the life
 *     of the account. e.g. 3% on Maven 10k, 6% on IF Micro 5k.
 *   • consistency: biggest_winning_day / total_profit ≤ threshold.
 *     Withdrawal gate, NOT a breach.
 *
 * Pair binding: hedges run mirrored 1:1 lots so the same $ swing hits
 * both legs. Smaller account binds. The pair's per-trade SL is the min
 * of both legs' floating caps, and the pair's days-to-withdrawal is
 * driven by the *stricter* of the two consistency thresholds.
 *
 * Canonical example (Maven 10k ↔ IF Micro 5k, default headroom 0.30):
 *   per-trade SL ≈ $35   (1% × $5k × 0.70)
 *   daily target ≈ $35   (1R hedge)
 *   days-to-first-dual-withdrawal = 7  (ceil(1 / 0.15))
 *   lifetime SL-hit budget = 8        (floor($300 / $35))
 */

export interface AccountRiskConfig {
  /** Initial deposit, fixed for the life of the account. Anchors lifetime DD. */
  startingBalance: number;
  /** Refreshes 1% floating-loss anchor after each closed position. */
  currentBalance: number;
  /** Live equity = balance + open floating PnL. */
  currentEquity: number;
  /** 1% per-position cap as a fraction. NULL for accounts without this rule. */
  maxFloatingPct: number | null;
  /** Daily DD as a fraction (0.02, 0.03, …). */
  dailyDdPct: number;
  /** Lifetime DD as a fraction (0.03, 0.06, …), anchored to startingBalance. */
  lifetimeDdPct: number;
  /** Consistency threshold as a fraction. NULL = no consistency rule. */
  consistencyPct: number | null;
  /** Equity at session open today. Used to compute daily DD remaining. */
  startOfDayEquity: number;
  /** Realised P&L since start-of-day. Negative = loss. */
  todayRealisedPnl: number;
  /** Largest single-day win across the account's lifetime, $. */
  biggestWinningDayUsd: number;
  /** Net of all daily P&Ls since the account opened, $. */
  totalProfitUsd: number;
}

export interface DerivedAccountRisk {
  /** Effective per-trade SL after slippage headroom. NULL if maxFloatingPct null. */
  perTradeSlMaxUsd: number | null;
  /** Daily DD budget remaining today, $. Floored at 0. */
  dailyDdRemainingUsd: number;
  /** Lifetime DD budget remaining, $. Floored at 0. */
  lifetimeDdRemainingUsd: number;
  /** Current consistency ratio (biggestDay / totalProfit). 1 if no profit yet. */
  consistencyRatio: number;
  /** Are we eligible to withdraw right now? */
  withdrawableNow: boolean;
  /**
   * Max realised gain we can take TODAY without breaching consistency.
   * Caps today's profit when it would otherwise become the new biggest
   * day and tip the ratio over threshold.
   */
  maxSafeTodayRealisedUsd: number;
  /** floor(lifetimeDdRemaining / perTradeSl). 0 if perTradeSl is null. */
  lifetimeSlHitBudget: number;
}

export interface PairRiskConfig {
  legA: AccountRiskConfig;
  legB: AccountRiskConfig;
  /** Default 0.30 = 70% effective. */
  slippageHeadroomPct?: number;
}

export interface PairDerivedRisk {
  /** min(legA.perTradeSl, legB.perTradeSl). */
  perTradeSlMaxUsd: number;
  /** 1R hedge → daily realised win ≈ perTradeSl, also bounded by maxSafeToday. */
  dailyTargetUsd: number;
  /** ceil(1 / strictest consistency). 7 for 15%, 5 for 20%. */
  daysToFirstDualWithdrawal: number;
  /** Which leg's per-trade SL constrains the pair. */
  bindingLeg: "A" | "B" | "tie";
  /** min(legA.lifetimeSlHitBudget, legB.lifetimeSlHitBudget). */
  lifetimeSlHitBudget: number;
  /** Per-account derived numbers, returned for UI display. */
  perLeg: { A: DerivedAccountRisk; B: DerivedAccountRisk };
  /** Human-readable annotations (e.g. "Leg B binds: smaller account"). */
  notes: string[];
}

const DEFAULT_SLIPPAGE_HEADROOM = 0.3;

export function deriveAccountRisk(
  cfg: AccountRiskConfig,
  slippageHeadroomPct: number = DEFAULT_SLIPPAGE_HEADROOM,
): DerivedAccountRisk {
  const headroom = clampFraction(slippageHeadroomPct);
  const effectiveFactor = 1 - headroom;

  const perTradeSlMaxUsd =
    cfg.maxFloatingPct == null
      ? null
      : Math.max(0, cfg.currentBalance * cfg.maxFloatingPct * effectiveFactor);

  const todayLossSoFar = Math.max(0, -cfg.todayRealisedPnl);
  const currentFloatingLoss = Math.max(0, cfg.currentBalance - cfg.currentEquity);
  const dailyBudgetGross = cfg.startOfDayEquity * cfg.dailyDdPct;
  const dailyDdRemainingUsd = Math.max(
    0,
    dailyBudgetGross - todayLossSoFar - currentFloatingLoss,
  );

  const lifetimeUsedUsd = Math.max(0, cfg.startingBalance - cfg.currentEquity);
  const lifetimeBudgetGross = cfg.startingBalance * cfg.lifetimeDdPct;
  const lifetimeDdRemainingUsd = Math.max(0, lifetimeBudgetGross - lifetimeUsedUsd);

  const consistencyRatio =
    cfg.totalProfitUsd > 0 ? cfg.biggestWinningDayUsd / cfg.totalProfitUsd : 1;

  const withdrawableNow =
    cfg.totalProfitUsd > 0 &&
    cfg.consistencyPct != null &&
    consistencyRatio <= cfg.consistencyPct;

  // Consistency math caps today's win only once the account has banked
  // *some* profit. A fresh account (totalProfit ≤ 0) hasn't established
  // a biggest-day baseline yet, so today's amount only matters relative
  // to the eventual total — future days dilute. Per-trade SL remains
  // the binding constraint in that state.
  //
  // Once totalProfit > 0:
  //   • If today (D) > existing biggest (B), it becomes the new biggest:
  //       D / (T + D) ≤ threshold  ⇒  D ≤ threshold·T / (1 − threshold)
  //   • If D ≤ B, the ratio only improves; any D up to B is safe.
  //   So safe-cap = max(B, T·threshold/(1−threshold)).
  let maxSafeTodayRealisedUsd: number;
  if (cfg.consistencyPct == null || cfg.totalProfitUsd <= 0) {
    maxSafeTodayRealisedUsd = Number.POSITIVE_INFINITY;
  } else {
    const threshold = cfg.consistencyPct;
    const safeAsNewBiggest =
      threshold < 1
        ? (threshold * cfg.totalProfitUsd) / (1 - threshold)
        : Number.POSITIVE_INFINITY;
    const safeUnderExisting = cfg.biggestWinningDayUsd;
    maxSafeTodayRealisedUsd = Math.max(safeUnderExisting, safeAsNewBiggest);
  }

  const lifetimeSlHitBudget =
    perTradeSlMaxUsd && perTradeSlMaxUsd > 0
      ? Math.floor(lifetimeDdRemainingUsd / perTradeSlMaxUsd)
      : 0;

  return {
    perTradeSlMaxUsd,
    dailyDdRemainingUsd,
    lifetimeDdRemainingUsd,
    consistencyRatio,
    withdrawableNow,
    maxSafeTodayRealisedUsd,
    lifetimeSlHitBudget,
  };
}

export function derivePairRisk(cfg: PairRiskConfig): PairDerivedRisk {
  const headroom = cfg.slippageHeadroomPct ?? DEFAULT_SLIPPAGE_HEADROOM;
  const A = deriveAccountRisk(cfg.legA, headroom);
  const B = deriveAccountRisk(cfg.legB, headroom);

  const slA = A.perTradeSlMaxUsd ?? Number.POSITIVE_INFINITY;
  const slB = B.perTradeSlMaxUsd ?? Number.POSITIVE_INFINITY;
  const perTradeSlMaxUsd = Math.min(slA, slB);

  const dailyTargetUsd = Math.min(
    perTradeSlMaxUsd,
    A.maxSafeTodayRealisedUsd,
    B.maxSafeTodayRealisedUsd,
  );

  const consistencies: number[] = [];
  if (cfg.legA.consistencyPct != null) consistencies.push(cfg.legA.consistencyPct);
  if (cfg.legB.consistencyPct != null) consistencies.push(cfg.legB.consistencyPct);
  const strictest = consistencies.length > 0 ? Math.min(...consistencies) : null;
  const daysToFirstDualWithdrawal = strictest ? Math.ceil(1 / strictest) : 0;

  let bindingLeg: "A" | "B" | "tie";
  if (slA < slB) bindingLeg = "A";
  else if (slB < slA) bindingLeg = "B";
  else bindingLeg = "tie";

  // Pair-level SL-hit budget uses the *pair's* per-trade SL on both
  // legs (mirrored 1:1 lots → same $ exposure both sides), not each
  // leg's individual cap. The binding leg burns at its own rate; the
  // looser leg burns at the same $ amount because lots match.
  const pairTradeSl = Number.isFinite(perTradeSlMaxUsd)
    ? perTradeSlMaxUsd
    : Number.POSITIVE_INFINITY;
  const aBudget =
    pairTradeSl > 0 && Number.isFinite(pairTradeSl)
      ? Math.floor(A.lifetimeDdRemainingUsd / pairTradeSl)
      : 0;
  const bBudget =
    pairTradeSl > 0 && Number.isFinite(pairTradeSl)
      ? Math.floor(B.lifetimeDdRemainingUsd / pairTradeSl)
      : 0;
  const lifetimeSlHitBudget = Math.min(aBudget, bBudget);

  const notes: string[] = [];
  if (bindingLeg !== "tie") {
    const smallerSize = Math.min(cfg.legA.startingBalance, cfg.legB.startingBalance);
    notes.push(
      `Leg ${bindingLeg} binds the per-trade SL (smaller floating cap on the $${smallerSize.toLocaleString()} side).`,
    );
  }
  if (strictest != null) {
    notes.push(
      `Stricter consistency rule = ${(strictest * 100).toFixed(0)}%. ${daysToFirstDualWithdrawal} equal-amount days minimum to dual-withdrawability.`,
    );
  }
  if (lifetimeSlHitBudget <= 3) {
    notes.push(
      `Only ${lifetimeSlHitBudget} SL-hits of life remain on the tighter leg — consider tightening per-trade SL.`,
    );
  }

  return {
    perTradeSlMaxUsd,
    dailyTargetUsd,
    daysToFirstDualWithdrawal,
    bindingLeg,
    lifetimeSlHitBudget,
    perLeg: { A, B },
    notes,
  };
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 0.95) return 0.95;
  return value;
}

/** Preset configs the dashboard uses to seed the calculator. */
export const RISK_PRESETS = {
  maven_10k: presetFromRules({
    startingBalance: 10000,
    maxFloatingPct: 0.01,
    dailyDdPct: 0.02,
    lifetimeDdPct: 0.03,
    consistencyPct: 0.2,
  }),
  if_micro_5k: presetFromRules({
    startingBalance: 5000,
    maxFloatingPct: 0.01,
    dailyDdPct: 0.03,
    lifetimeDdPct: 0.06,
    consistencyPct: 0.15,
  }),
} as const satisfies Record<string, AccountRiskConfig>;

function presetFromRules(rules: {
  startingBalance: number;
  maxFloatingPct: number | null;
  dailyDdPct: number;
  lifetimeDdPct: number;
  consistencyPct: number | null;
}): AccountRiskConfig {
  return {
    ...rules,
    currentBalance: rules.startingBalance,
    currentEquity: rules.startingBalance,
    startOfDayEquity: rules.startingBalance,
    todayRealisedPnl: 0,
    biggestWinningDayUsd: 0,
    totalProfitUsd: 0,
  };
}
